"use client";

import { clsx } from "clsx";
import { ScrollArea } from "@base-ui/react/scroll-area";
import { BlossomCarousel } from "./BlossomCarousel";
import { Button } from "../core/Button";
import { useCallback, useEffect, useRef, useState } from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import IconChevronLeftMedium from "@ui/central-icons/icons/IconChevronLeftMedium";
import IconChevronRightMedium from "@ui/central-icons/icons/IconChevronRightMedium";
import { useTranslations } from "next-intl";
import { type ScrollGridProps } from "./ScrollGrid";
import { LegendScroller } from "../core/LegendScroller";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { TabList } from "../core/react-aria/Tabs";

interface ScrollGridContainerProps
  extends
    Required<Pick<ScrollGridProps<unknown>, "columns">>,
    Pick<ScrollGridProps<unknown>, "alignNavButtonsToRatio"> {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  itemCount: number;
  scrollbar: "none" | "normal" | "thin";
  snap: boolean;
  draggable: boolean;
  peek: boolean;
  navButtons: boolean;
  legend?: { titles: string[]; textClassName?: string; className?: string };
  as?: "list" | "tabs";
  /** Ref to the horizontal scroll viewport (the list / carousel scroller). */
  ref?: React.Ref<HTMLElement>;
  onChangeLeftmostIndex?: (index: number) => void;
}

/**
 * Client component wrapper for `ScrollGrid`
 */
export function ScrollGridContainer({
  children,
  className,
  scrollbar,
  snap,
  draggable,
  itemCount,
  columns,
  navButtons,
  alignNavButtonsToRatio,
  peek,
  style,
  legend,
  as = "list",
  ref,
  onChangeLeftmostIndex,
}: ScrollGridContainerProps) {
  const Root = scrollbar !== "none" ? ScrollArea.Root : "div";
  const viewportRef = useRef<HTMLElement | null>(null);

  const setViewportRef = useCallback(
    (node: HTMLElement | null) => {
      viewportRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );
  const [showPreviousButton, setShowPreviousButton] = useState(false);
  const [showNextButton, setShowNextButton] = useState(true);
  const [leftmostIndex, setLeftmostIndex] = useState(0);
  const lastReportedLeftmostIndexRef = useRef(0);

  const viewportProps = {
    ref: setViewportRef,
    children,
    role: as === "list" ? "list" : undefined,
    className: clsx(
      "!tw-flex tw-gap-[var(--gap)] tw-outline-none tw-scroll-px-full-bleed !tw-px-full-bleed tw-py-4 -tw-my-4 tw-overflow-x-auto tw-scrollbar-none",
      snap && "tw-snap-x tw-snap-mandatory",
    ),
    onScroll: () => {
      checkNavButtonVisibility();
      if (legend || onChangeLeftmostIndex) {
        let currentIndex = getCurrentIndex();
        const scrollWidth = viewportRef.current?.scrollWidth ?? 0;
        const clientWidth = viewportRef.current?.clientWidth ?? 0;
        const scrollLeft = viewportRef.current?.scrollLeft ?? 0;
        if (Math.round(scrollLeft + clientWidth) >= Math.round(scrollWidth)) {
          const visibleCols = getVisibleColumnCount();
          currentIndex = Math.max(0, itemCount - visibleCols);
        }
        setLeftmostIndex(currentIndex);
        if (onChangeLeftmostIndex && currentIndex !== lastReportedLeftmostIndexRef.current) {
          lastReportedLeftmostIndexRef.current = currentIndex;
          onChangeLeftmostIndex(currentIndex);
        }
      }
    },
  };

  useEffect(() => {
    if (!viewportRef.current) return;
    checkNavButtonVisibility();
    const observer = new ResizeObserver(() => {
      checkNavButtonVisibility();
    });
    observer.observe(viewportRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  function checkNavButtonVisibility() {
    const scrollWidth = viewportRef.current?.scrollWidth ?? 0;
    const clientWidth = viewportRef.current?.clientWidth ?? 0;
    const scrollLeft = viewportRef.current?.scrollLeft ?? 0;
    if (Math.round(scrollLeft) <= 0) {
      setShowPreviousButton(false);
    } else {
      setShowPreviousButton(true);
    }
    if (Math.round(scrollLeft + clientWidth) >= Math.round(scrollWidth)) {
      setShowNextButton(false);
    } else {
      setShowNextButton(true);
    }
  }

  function scrollToChild(child: Element) {
    const container = viewportRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const paddingLeft = parseFloat(getComputedStyle(container).paddingLeft) || 0;
    const childRect = child.getBoundingClientRect();
    container.scrollTo({
      left: container.scrollLeft + childRect.left - containerRect.left - paddingLeft,
      behavior: "smooth",
    });
  }

  function getContentLeftEdge() {
    const container = viewportRef.current;
    if (!container) return 0;
    const containerRect = container.getBoundingClientRect();
    const paddingLeft = parseFloat(getComputedStyle(container).paddingLeft) || 0;
    return containerRect.left + paddingLeft;
  }

  function getCurrentIndex() {
    const container = viewportRef.current;
    if (!container) return 0;
    const leftEdge = getContentLeftEdge();
    for (let i = container.children.length - 1; i >= 0; i--) {
      if (container.children[i].getBoundingClientRect().left <= leftEdge + 1) {
        return i;
      }
    }
    return 0;
  }

  /** Matches ScrollGrid’s responsive `--cols` (inherited on the viewport), not the desktop `columns` prop. */
  function getVisibleColumnCount() {
    const container = viewportRef.current;
    if (!container) return columns;
    const raw = getComputedStyle(container).getPropertyValue("--cols").trim();
    if (!raw) return columns;
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n < 1) return columns;
    return Math.min(itemCount, Math.round(n));
  }

  function scrollToNext() {
    const child = viewportRef.current?.children[getCurrentIndex() + 1];
    if (child) scrollToChild(child);
  }

  function scrollToPrevious() {
    const container = viewportRef.current;
    if (!container) return;
    const index = getCurrentIndex();
    const current = container.children[index];
    if (!current) return;
    const isClipped = current.getBoundingClientRect().left < getContentLeftEdge() - 1;
    const target = container.children[isClipped ? index : index - 1];
    if (target) scrollToChild(target);
  }

  const List = as === "tabs" ? TabList : "ul";

  return (
    <>
      <Root className={clsx("tw-relative -tw-mx-full-bleed tw-flow-root", className)} style={style}>
        {navButtons && itemCount > 2 && (
          <>
            <NavigationButton
              visible={showPreviousButton}
              variant='previous'
              columns={columns}
              itemCount={itemCount}
              peek={peek}
              alignToRatio={alignNavButtonsToRatio}
              onClick={() => {
                scrollToPrevious();
              }}
            />
            <NavigationButton
              visible={showNextButton}
              variant='next'
              columns={columns}
              itemCount={itemCount}
              peek={peek}
              alignToRatio={alignNavButtonsToRatio}
              onClick={() => {
                scrollToNext();
              }}
            />
          </>
        )}
        <div className='tw-overflow-hidden tw-py-6 -tw-my-6 has-[>:focus-visible]:tw-outline-focus tw-rounded-sm'>
          {scrollbar !== "none" ? (
            <ScrollArea.Viewport
              {...mergeProps(viewportProps, {})}
              render={draggable ? <BlossomCarousel as={as} /> : <List />}
            />
          ) : draggable ? (
            <BlossomCarousel as={as} {...viewportProps} />
          ) : (
            <List {...viewportProps} />
          )}
        </div>
        {scrollbar !== "none" && (
          <ScrollArea.Scrollbar
            orientation='horizontal'
            className={clsx(
              "tw-relative tw-z-10 tw-group tw-pointer-events-none tw-flex tw-items-end tw-mx-full-bleed tw-rounded-full tw-opacity-0 tw-transition-opacity data-[hovering]:tw-pointer-events-auto data-[scrolling]:tw-pointer-events-auto data-[hovering]:tw-opacity-100 data-[scrolling]:tw-opacity-100 data-[hovering]:tw-delay-0 data-[scrolling]:tw-duration-0",
              scrollbar === "thin" ? "tw-h-4 -tw-mb-4" : "tw-h-10 -tw-mb-10",
            )}
          >
            <ScrollArea.Thumb className={clsx("tw-flex tw-items-center", scrollbar === "thin" ? "tw-h-4" : "tw-h-10")}>
              <div
                className={clsx(
                  "tw-w-full tw-rounded-full tw-bg-gray-200",
                  scrollbar === "thin" ? "tw-h-0.5 group-hover:tw-h-1" : "tw-h-1 group-hover:tw-h-1.5",
                )}
              />
            </ScrollArea.Thumb>
          </ScrollArea.Scrollbar>
        )}
      </Root>
      {legend && legend.titles.length > 0 && (
        <div className={legend.className ?? "tw-mt-8"}>
          <LegendScroller
            titles={legend.titles}
            leftmostIndex={leftmostIndex}
            onItemClick={index => {
              const child = viewportRef.current?.children[index];
              if (child) scrollToChild(child);
            }}
            columns={columns}
            textClassName={legend.textClassName}
          />
        </div>
      )}
    </>
  );
}

function NavigationButton({
  variant,
  columns,
  itemCount,
  visible,
  onClick,
  peek,
  alignToRatio,
}: {
  variant: "previous" | "next";
  columns: ScrollGridContainerProps["columns"];
  itemCount: number;
  visible: boolean;
  onClick?: () => void;
  peek?: boolean;
  alignToRatio?: number;
}) {
  const t = useTranslations("ClientKeys.Globals");
  const Icon = variant === "previous" ? IconChevronLeftMedium : IconChevronRightMedium;

  return (
    <div
      inert={!visible}
      style={{ "--ratio": alignToRatio } as React.CSSProperties}
      className={clsx(
        !visible && "tw-opacity-0",
        "tw-z-10 tw-absolute tw-top-1/2 -tw-mt-4 tw-hidden [@media(hover:hover)_and_(pointer:fine)]:tw-block tw-transition-opacity",
        alignToRatio && "supports-[container-type:inline-size]:tw-top-[calc(var(--w)/2/var(--ratio))]",
        variant === "previous"
          ? "tw-right-full -tw-mr-4 sm:-tw-mr-2 xl:tw-mr-4"
          : "tw-left-full -tw-ml-4 sm:-tw-ml-2 xl:tw-ml-4",
        columns === 3 && [
          itemCount > 2 ? "md:[@media(hover:hover)_and_(pointer:fine)]:tw-block" : "md:!tw-hidden",
          itemCount > 3
            ? peek
              ? "lg:[@media(hover:hover)_and_(pointer:fine)]:tw-block"
              : "lg:!tw-block"
            : "lg:!tw-hidden",
        ],
        columns === 4 && [
          itemCount > 3 ? "lg:[@media(hover:hover)_and_(pointer:fine)]:tw-block" : "lg:!tw-hidden",
          itemCount > 4
            ? peek
              ? "xl:[@media(hover:hover)_and_(pointer:fine)]:tw-block"
              : "xl:!tw-block"
            : "xl:!tw-hidden",
        ],
        columns === 5 && [
          itemCount > 4 ? "lg:[@media(hover:hover)_and_(pointer:fine)]:tw-block" : "lg:!tw-hidden",
          itemCount > 5
            ? peek
              ? "xl:[@media(hover:hover)_and_(pointer:fine)]:tw-block"
              : "xl:!tw-block"
            : "xl:!tw-hidden",
        ],
      )}
    >
      <Button
        size='pill-md'
        variant='elevated'
        aria-label={variant === "previous" ? t("previous") : t("next")}
        icon={<Icon className={clsx("tw-size-4", iconStrokeFix)} />}
        onClick={onClick}
      >
        <div className='tw-absolute -tw-inset-4' />
      </Button>
    </div>
  );
}
